import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { 
  CaseDetails, 
  CaseDetailsWithEmailResult, 
  EmployeeInfoForm, 
  EmployeeSelectionDetails, 
  MissingDetails, 
  NewEmployeeGroupForm, 
  NewEmployeeWithoutContactInfoForm, 
  SelectionOption, 
  StiiraError } from '@core/models';
import { AnonymousEmployee } from '@core/models/leave-admin/anonymous-request/anonymous-employee.model';
import { EmployeeInfoPost, NewEmployee, NewEmployeeWithoutContactInfo } from '@core/models/leave-admin/employee-info-post.model';
import { EmployeePost } from '@core/models/leave-admin/employee-post.model';
import { DuplicateDetectedDialog } from '@core/models/leave-admin/employees/duplicate-detected-dialog.model';
import { ErrorService, LayoutService, NotificationService, SnackbarService } from '@core/services';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { Subject, Observable } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { UpdateConfirmationComponent, UpdateConfirmationDialogAction } from '../update-confirmation/update-confirmation.component';
import { DuplicateDetectedDialogComponent } from '../duplicate-detected-dialog/duplicate-detected-dialog.component';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { StateAbbreviation } from '@core/models/leave-admin/employers/state-abbreviation.model';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';
import { EmailResult } from '@core/enums';
import { buildUserDefinedFieldsPost, initUserDefinedFieldsArray, positionUserDefinedFields} from '@shared/helpers/user-defined-fields.helpers';
import { UserDefinedFieldPosition } from '@core/enums/user-defined-field-position.enum';

@Component({
    selector: 'app-leave-employee-info',
    templateUrl: './leave-employee-info.component.html',
    styleUrls: ['./leave-employee-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeaveEmployeeInfoComponent implements OnInit {
  static readonly componentName = 'LeaveEmployeeInfoComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  @ViewChild('addSupervisorButton', { read: ElementRef }) private addSupervisorButton: ElementRef<HTMLButtonElement>;

  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public showNotifyToggle$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup<EmployeeInfoForm>;
  public isNewSupervisor: boolean[] = [];
  public isNewHrManager: boolean = false;
  public isNewEmployee: boolean = false;
  public anonEmployee: AnonymousEmployee;
  public emitInputChange: string;
  public isLoading$: Subject<boolean> = new Subject<boolean>();
  public userDefinedFieldPosition = UserDefinedFieldPosition;
  public position = position;
  public onDragEnded = onDragEnded;
  public positionUserDefinedFields = positionUserDefinedFields;

  private formInitValues: any;
  private formChangeEmitted: boolean = false;
  private updateEERecord: EmployeeSelectionDetails;
  private destroy$: Subject<void> = new Subject<void>();
  
  public isMobile$: Observable<boolean>;

  get newEmployeeSupervisor(): FormGroup<NewEmployeeGroupForm> {
    return this.form.controls.newEmployeeSupervisor;
  }

  get supervisorsArray(): FormArray<FormGroup<NewEmployeeGroupForm>> {
    return this.form.controls.supervisors;
  } 

  get newEmployeeHrManager(): FormGroup<NewEmployeeGroupForm> {
    return this.form.controls.newEmployeeHrManager;
  }

  get newEmployee(): FormGroup<NewEmployeeWithoutContactInfoForm> {
    return this.form.controls.newEmployee;
  }

  get employeeField(): FormControl<number> {
    return this.form.controls.employee;
  }

  get employeeWorkEmailField(): FormControl<string> {
    return this.form.controls.workEmail;
  }

  get employeePersonalEmailField(): FormControl<string> {
    return this.form.controls.personalEmail;
  }

  get newEmployeeHrMgrPersonalEmailField(): FormControl<string> {
    return this.newEmployeeHrManager.controls.personalEmail;
  }

  get newEmployeeHrMgrWorkEmailField(): FormControl<string> {
    return this.newEmployeeHrManager.controls.workEmail;
  }

  get employeeNameField(): FormControl<string> {
    return this.form.controls.employeeName;
  }

  get companyField(): FormControl<string> {
    return this.form.controls.company;
  }

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { 
      caseDetails: CaseDetails; 
      sysText: any; 
      employeeOptions: SelectionOption[];
      workLocations: string[];
      jobTitles: string[];
      stateAbbreviations: StateAbbreviation[];
      stateOptions: SelectionOption[];
      supervisorOptions: SelectionOption[];
      hrManagerOptions: SelectionOption[];
      employmentStatuses: string[];
      employeeTypes: string[];
      payTypes: string[];
      fieldNotifications: { [key: string]: string };
      canChangeEmployee: boolean;
      duplicateDetectedSysText: any;
      updateConfSysText: any;
      showNotificationToggle: boolean;
    },
    private fb: FormBuilder,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private eeInfoDialogRef: MatDialogRef<LeaveEmployeeInfoComponent>,
    private service: LeaveAdminService,
    private store: LeaveAdminStoreService,
    private layoutService: LayoutService,
    private snackbar: SnackbarService,
    private notificationService: NotificationService
  ) {
    this.anonEmployee = data.caseDetails.employeeInformation.anonymousEmployee;
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.eeInfoDialogRef, this.isSubmitting$, () => this.close(false));

    this.setupForm();

    if (!this.data.canChangeEmployee) {
      this.employeeField.disable();
    }
    else {
      this.employeeField.setValidators(Validators.required);
    }
    
    this.companyField.disable();
    this.employeeNameField.disable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.employeeField.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        if (!this.isNewEmployee) {
          this.patchFormFields(val);
        }

        this.handleNotifyToggle();
      });
  }

  public setupForm() {
    this.form = this.fb.group<EmployeeInfoForm>({
      employee: this.fb.control(null),
      employeeName: this.fb.control(null),
      workEmail: this.fb.control(null),
      workEmailChecked: this.fb.control(null),
      personalEmail: this.fb.control(null),
      personalEmailChecked: this.fb.control(null),
      workPhone: this.fb.control(null),
      personalPhone: this.fb.control(null),
      company: this.fb.control(null), 
      workLocation: this.fb.control(null),
      jobTitle: this.fb.control(null),
      city: this.fb.control(null), 
      stateId: this.fb.control(null),
      supervisors: this.fb.array([this.initNewEEGroup()]),
      hrManagerId: this.fb.control(null),

      employmentStatus: this.fb.control(null),
      employeeType: this.fb.control(null),
      payType: this.fb.control(null),

      hireDate: this.fb.control(null), 
      originalHireDate: this.fb.control(null), 
      lastTerminationDate: this.fb.control(null), 

      fixedLeaveYearStart: this.fb.control(null),
      bank1FirstLeaveUsageDate: this.fb.control(null),
      bank2FirstLeaveUsageDate: this.fb.control(null),
      bank3FirstLeaveUsageDate: this.fb.control(null),
      bank4FirstLeaveUsageDate: this.fb.control(null),
      bank5FirstLeaveUsageDate: this.fb.control(null),

      newEmployee: this.initNewEEWithoutContactGroup(),
      newEmployeeSupervisor: this.initNewEEGroup(),
      newEmployeeHrManager: this.initNewEEGroup(),

      sendNotification: this.fb.control(false),

      userDefinedFields: initUserDefinedFieldsArray(this.data.caseDetails.employeeInformation.userDefinedFields),
    });

    this.form.patchValue({
      employee: +this.data.caseDetails.employeeInformation?.employee?.id,
      employeeName: this.data.caseDetails.employeeInformation?.employee?.description,
      workEmail: this.data.caseDetails.employeeInformation?.employee?.workEmail,
      workEmailChecked: this.data.caseDetails.employeeInformation?.employee?.primaryEmailIsPersonal == false,
      personalEmail: this.data.caseDetails.employeeInformation?.employee?.personalEmail,
      personalEmailChecked: this.data.caseDetails.employeeInformation?.employee?.primaryEmailIsPersonal == true,
      workPhone: this.data.caseDetails.employeeInformation?.workPhoneNumber,
      personalPhone: this.data.caseDetails.employeeInformation?.personalPhoneNumber,

      company: this.data.caseDetails.company.description,
      workLocation: this.data.caseDetails.employeeInformation?.workLocation,
      jobTitle: this.data.caseDetails.employeeInformation?.jobTitle,
      city: this.data.caseDetails.employeeInformation?.city,
      stateId: this.data.caseDetails.employeeInformation?.stateId,
      hrManagerId: +this.data.caseDetails.employeeInformation?.hrManager?.id,

      employmentStatus: this.data.caseDetails.employeeInformation?.employmentStatus,
      employeeType: this.data.caseDetails.employeeInformation?.employeeType,
      payType: this.data.caseDetails.employeeInformation?.payType,

      hireDate: this.data.caseDetails.employeeInformation?.hireDate,
      originalHireDate: this.data.caseDetails.employeeInformation?.originalHireDate,
      lastTerminationDate: this.data.caseDetails.employeeInformation?.lastTerminationDate,

      fixedLeaveYearStart: this.data.caseDetails.employeeInformation?.fixedLeaveYearStart,
      bank1FirstLeaveUsageDate: this.data.caseDetails.employeeInformation?.bank1FirstLeaveUsageDate,
      bank2FirstLeaveUsageDate: this.data.caseDetails.employeeInformation?.bank2FirstLeaveUsageDate,
      bank3FirstLeaveUsageDate: this.data.caseDetails.employeeInformation?.bank3FirstLeaveUsageDate,
      bank4FirstLeaveUsageDate: this.data.caseDetails.employeeInformation?.bank4FirstLeaveUsageDate,
      bank5FirstLeaveUsageDate: this.data.caseDetails.employeeInformation?.bank5FirstLeaveUsageDate
    });

    // handle patching supervisors
    for (let i = 1; i < this.data.caseDetails.employeeInformation?.supervisorHierarchy?.filter(sh => sh.depth == 1)?.length; i++) {
      this.addSupervisorFormGroup();
    }

    if (this.data.caseDetails.employeeInformation?.supervisorHierarchy?.filter(sh => sh.depth == 1)?.length > 0) {
      this.supervisorsArray.controls.forEach((obj, index) => {
        if (this.data.caseDetails.employeeInformation.supervisorHierarchy.filter(sh => sh.depth == 1)[index] != null) {
          obj.patchValue({employeeId: this.data.caseDetails.employeeInformation.supervisorHierarchy.filter(sh => sh.depth == 1)[index].employeeID});
        }
      });
    }

    this.missingDetailsErrorsSetTimeout();

    setTimeout(() => {
      this.formInitValues = JSON.parse(JSON.stringify(this.form.value, noChangesReplacer));
      this.handleNotifyToggle();
      
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public initNewEEGroup(): FormGroup<NewEmployeeGroupForm>{
    return this.fb.group<NewEmployeeGroupForm>({
      employeeId: this.fb.control(null),
      firstName: this.fb.control(null),
      middleName: this.fb.control(null),
      lastName: this.fb.control(null),
      suffix: this.fb.control(null),
      preferredName: this.fb.control(null),
      workEmail: this.fb.control(null),
      workEmailChecked: this.fb.control(null),
      personalEmail: this.fb.control(null),
      personalEmailChecked: this.fb.control(null),
      primaryEmailIsPersonal: this.fb.control(null),
      workPhone: this.fb.control(null),
      personalPhone: this.fb.control(null),
    })
  }

  public initNewEEWithoutContactGroup(): FormGroup<NewEmployeeWithoutContactInfoForm>{
    return this.fb.group<NewEmployeeWithoutContactInfoForm>({
      firstName: this.fb.control(''),
      middleName: this.fb.control(''),
      lastName: this.fb.control(''),
      suffix: this.fb.control(''),
      preferredName: this.fb.control('')
    })
  }

  public addSupervisorFormGroup(): FormGroup<NewEmployeeGroupForm> {
    const createContactGroup = this.initNewEEGroup();
    this.supervisorsArray.push(createContactGroup);
    this.isNewSupervisor.push(false);
    return createContactGroup;
  }

  public onEEHintClick(): void {
    this.isNewEmployee = !this.isNewEmployee;

    if (!this.data.caseDetails.employeeInformation.isAnonymous) {
      this.form.controls.newEmployee.patchValue({ 
        firstName: '',
        middleName: '',
        lastName: '',
        suffix: '',
        preferredName: ''
      });
    }

    if (this.isNewEmployee) {
      if (this.data.caseDetails.employeeInformation.isAnonymous) {
        this.form.controls.newEmployee.patchValue({ 
          firstName: this.anonEmployee.firstName,
          middleName: this.anonEmployee.middleName,
          lastName: this.anonEmployee.lastName,
          suffix: this.anonEmployee.suffix,
          preferredName: ''
        });

        this.form.patchValue({
          workEmail:  this.anonEmployee.workEmail,
          workEmailChecked: (this.anonEmployee.primaryEmailIsPersonal == false),
          personalEmail:  this.anonEmployee.personalEmail,
          personalEmailChecked: (this.anonEmployee.primaryEmailIsPersonal == true),
          workPhone: this.anonEmployee.workPhone,
          personalPhone: this.anonEmployee.personalPhone,
        });
      }
      else {
        this.employeeField.setValidators(null);
        this.newEmployee.controls.firstName.setValidators(Validators.required);
        this.newEmployee.controls.lastName.setValidators(Validators.required);
        this.employeeField.setValue(null);
        this.clearFormFields();
      }
    }
    else {
      this.employeeField.setValidators(Validators.required);
      this.newEmployee.controls.firstName.setValidators(null);
      this.newEmployee.controls.lastName.setValidators(null);

      if (!this.data.caseDetails.employeeInformation.isAnonymous) {
        this.form.patchValue({
          employee: +this.data.caseDetails.employeeInformation?.employee?.id,
          workEmailChecked: (this.data.caseDetails.employeeInformation?.employee?.primaryEmailIsPersonal == false),
          personalEmailChecked: (this.data.caseDetails.employeeInformation?.employee?.primaryEmailIsPersonal == true),
        }, {emitEvent: false})
      }

      this.clearFormFields();

      this.form.updateValueAndValidity();
      this.form.markAsUntouched();
    }
  }

  public onAddSupervisorContact(index: number): void {
    this.isNewSupervisor[index] = !this.isNewSupervisor[index];
    const formGroup: FormGroup = this.supervisorsArray.controls[index] as FormGroup;

    formGroup.patchValue({
      firstName: null,
      middleName: null,
      lastName: null,
      suffix: null,
      prefferedName: null,
      workEmail: null,
      workEmailChecked: null,
      personalEmail: null,
      personalEmailChecked: null,
      workPhone: null,
      personalPhone: null,
    });

    if (this.isNewSupervisor[index]) {
      formGroup.controls.firstName.setValidators(Validators.required)
      formGroup.controls.lastName.setValidators(Validators.required)
    }
    else {
      formGroup.controls.firstName.setValidators(null);
      formGroup.controls.lastName.setValidators(null);
    }

    formGroup.updateValueAndValidity();
    formGroup.markAsUntouched();
  }

  public onRemoveSupervisorContact(index: number) {
    // the pressed button goes with its row
    this.addSupervisorButton.nativeElement.focus();
    this.isNewSupervisor.splice(index, 1);
    this.supervisorsArray.removeAt(index);
  }

  public onHrManagerHintClick(): void {
    this.isNewHrManager = !this.isNewHrManager

    this.form.controls.newEmployeeHrManager.patchValue({
      firstName: null,
      middleName: null,
      lastName: null,
      suffix: null,
      preferredName: null,
      workEmail: null,
      workEmailChecked: null,
      personalEmail: null,
      personalEmailChecked: null,
      workPhone: null,
      personalPhone: null,
    });

    if (this.isNewHrManager) {
      this.newEmployeeHrManager.controls.firstName.setValidators(Validators.required);
      this.newEmployeeHrManager.controls.lastName.setValidators(Validators.required);
    }
    else {
      this.newEmployeeHrManager.controls.firstName.setValidators(null);
      this.newEmployeeHrManager.controls.lastName.setValidators(null);
    }

    this.newEmployeeHrManager.updateValueAndValidity();
    this.newEmployeeHrManager.markAsUntouched();
  }

  private missingDetailsErrorsSetTimeout(missingDetails?: MissingDetails) {
    setTimeout(() => {
      if (missingDetails != null) {
        this.errorService.setFormModelStateErrors(this.form, missingDetails);
      }
      else if (this.data.caseDetails?.missingDetails) {
        this.errorService.setFormModelStateErrors(this.form, this.data.caseDetails.missingDetails);
      }
    }, 0);
  }

  public onSubmit(
    overrideDuplicates: boolean = false, 
    overrideUpdateConf: boolean = false, 
    addSupervisorRoles: boolean = null): void {
    this.form.markAsUntouched();

    if (this.store.isActionRunning) {
      this.openPendingChangesDialog(overrideDuplicates, overrideUpdateConf, addSupervisorRoles);
      return;
    }

    let cleanSupervisors: NewEmployee[] = [];
    this.supervisorsArray.controls.forEach((formGroup: FormGroup, index) => {
      let isEmpty : boolean;
      Object.keys(formGroup.controls).forEach((control: string) => {
        const val = formGroup.controls[control].value;
        if (val){
          isEmpty = false;
        }
      });

      if (isEmpty == false) {
        const newSup: NewEmployee = {
          employeeId: this.isNewSupervisor[index] ? null : formGroup.controls.employeeId.value,
          firstName: this.isNewSupervisor[index] ? formGroup.controls.firstName.value : null,
          middleName: this.isNewSupervisor[index] ? formGroup.controls.middleName.value : null,
          lastName: this.isNewSupervisor[index] ? formGroup.controls.lastName.value : null,
          suffix: this.isNewSupervisor[index] ? formGroup.controls.suffix.value : null,
          preferredName: this.isNewSupervisor[index] ? formGroup.controls.preferredName.value : null,
          workEmail: !this.isNewSupervisor[index] || formGroup.controls.workEmail.value?.trim().length == 0 ? null : formGroup.controls.workEmail.value,
          personalEmail: !this.isNewSupervisor[index] || formGroup.controls.personalEmail.value?.trim().length == 0 ? null : formGroup.controls.personalEmail.value,
          primaryEmailIsPersonal: this.isNewSupervisor[index] 
            ? formGroup.controls.personalEmailChecked.value 
              ? true 
              : formGroup.controls.workEmailChecked.value 
                ? false 
                : null
            : null,
          workPhone: this.isNewSupervisor[index] ? formGroup.controls.workPhone.value : null,
          personalPhone: this.isNewSupervisor[index] ? formGroup.controls.personalPhone.value : null,
        };

        cleanSupervisors.push(newSup);
      }
    });

    const newHrMan: NewEmployee = {
      employeeId: this.isNewHrManager ? null : this.newEmployeeHrManager.controls.employeeId.value,
      firstName: this.newEmployeeHrManager.controls.firstName.value,
      middleName: this.newEmployeeHrManager.controls.middleName.value,
      lastName: this.newEmployeeHrManager.controls.lastName.value,
      suffix: this.newEmployeeHrManager.controls.suffix.value,
      preferredName: this.newEmployeeHrManager.controls.preferredName.value,
      workEmail: this.newEmployeeHrManager.controls.workEmail.value,
      personalEmail: this.newEmployeeHrManager.controls.personalEmail.value,
      primaryEmailIsPersonal: this.newEmployeeHrManager.controls.workEmailChecked.value
        ? false
        : this.newEmployeeHrManager.controls.personalEmailChecked.value
          ? true
          : null,
      workPhone: this.newEmployeeHrManager.controls.workPhone.value,
      personalPhone: this.newEmployeeHrManager.controls.personalPhone.value
    }

    const newEe: NewEmployeeWithoutContactInfo = {
      firstName: this.newEmployee.controls.firstName.value,
      middleName: this.newEmployee.controls.middleName.value,
      lastName: this.newEmployee.controls.lastName.value,
      suffix: this.newEmployee.controls.suffix.value,
      preferredName: this.newEmployee.controls.preferredName.value
    }

    const ep: EmployeePost = {
      employeeId: this.isNewEmployee ? null : this.employeeField.value,
      newEmployee: this.isNewEmployee ? newEe : null
    }

    const dto: EmployeeInfoPost = {
      overrideDuplicates: overrideDuplicates,
      overrideUpdateConf: overrideUpdateConf,
      addSupervisorRoles: addSupervisorRoles,

      caseId: this.data.caseDetails.leaveInformation?.caseId,

      employee: ep, 
      workEmail: this.form.controls.workEmail.value,
      personalEmail: this.form.controls.personalEmail.value,
      primaryEmailIsPersonal: this.form.controls.personalEmailChecked.value ? true : this.form.controls.workEmailChecked.value ? false : null,
      workPhone: this.form.controls.workPhone.value, 
      personalPhone: this.form.controls.personalPhone.value, 

      workLocation: this.form.controls.workLocation.value,
      jobTitle: this.form.controls.jobTitle.value,
      city: this.form.controls.city.value, 
      stateId: this.form.controls.stateId.value,
      supervisors: cleanSupervisors,
      hrManagerId: this.isNewHrManager ? null : this.form.controls.hrManagerId.value, 
      newEmployeeHrManager: this.isNewHrManager ? newHrMan : null, 

      employmentStatus: this.form.controls.employmentStatus.value,
      employeeType: this.form.controls.employeeType.value,
      payType: this.form.controls.payType.value,

      hireDate: this.form.controls.hireDate.value, 
      originalHireDate: this.form.controls.originalHireDate.value, 
      lastTerminationDate: this.form.controls.lastTerminationDate.value, 

      fixedLeaveYearStart: this.form.controls.fixedLeaveYearStart.value,
      bank1FirstLeaveUsageDate: this.form.controls.bank1FirstLeaveUsageDate.value,
      bank2FirstLeaveUsageDate: this.form.controls.bank2FirstLeaveUsageDate.value,
      bank3FirstLeaveUsageDate: this.form.controls.bank3FirstLeaveUsageDate.value,
      bank4FirstLeaveUsageDate: this.form.controls.bank4FirstLeaveUsageDate.value,
      bank5FirstLeaveUsageDate: this.form.controls.bank5FirstLeaveUsageDate.value,
      
      sendNotification: this.showNotifyToggle() ? this.form.controls.sendNotification.value : null,

      userDefinedFields: buildUserDefinedFieldsPost(this.form.controls.userDefinedFields)
    }

    this.service.postEmployeeInfo(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe(
        (res) => {
          if (res.showDuplicateDialog) {
            this.openDuplicateDetectedDialog(res as DuplicateDetectedDialog, overrideUpdateConf);
          }
          else if (res.showUpdateConfirmationDialog) {
            this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog, overrideDuplicates, overrideUpdateConf);
          }
          else {
            const caseDetailsWithEmailResult = res as CaseDetailsWithEmailResult;

            if (caseDetailsWithEmailResult.emailResult == EmailResult.success) {
              let snackText = this.data.sysText.caseEmployeeInfo.caseEmployeeInfoWithEmailSuccess;
              this.snackbar.open(snackText, this.data.sysText.caseEmployeeInfo.dismiss);
            }
            else if (caseDetailsWithEmailResult.emailResult == EmailResult.successWithFailure) {
              let errorText = this.data.sysText.caseEmployeeInfo.caseEmployeeInfoWithEmailSuccessFailure;
              this.notificationService.showErrorToast(errorText);
            }
            else if (caseDetailsWithEmailResult.emailResult == EmailResult.failure) {
              let errorText = this.data.sysText.caseEmployeeInfo.caseEmployeeInfoWithEmailFailure;
              this.notificationService.showErrorToast(errorText);
            }
            else {
              let snackText = this.data.sysText.caseEmployeeInfo.caseEmployeeInfoWithoutEmail;
              this.snackbar.open(snackText, this.data.sysText.caseEmployeeInfo.dismiss);
            }

            this.store.caseDetails = caseDetailsWithEmailResult.leaveCaseDetails;
            this.close(true);
          }
        },
        (err: StiiraError) =>
          this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
      );
  }

  public close(canNavigate: boolean): void {
    if (canNavigate){
      this.eeInfoDialogRef.close();
    }
    else {
      if (this.noChanges) {
        this.eeInfoDialogRef.close();
      }
      else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  public showNotifyToggle() : boolean {
    var showToggle = this.data.showNotificationToggle == true
      && this.formInitValues != null
      && (this.isNewEmployee == true || this.employeeField.value > 0);
      
    return showToggle;
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.sysText.unsavedChanges,
    };

    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.eeInfoDialogRef.close();
        }
      });
  }

  private clearFormFields(): void {
    this.form.patchValue({
      employeeName: null,
      workEmail: null,
      workEmailChecked: null,
      personalEmail: null,
      personalEmailChecked: null,
      workPhone: null,
      personalPhone: null,

      workLocation: null,
      jobTitle: null,
      city: null,
      stateId: null,
      hrManagerId: null,

      employmentStatus: null,
      employeeType: null,
      payType: null,

      hireDate: null,
      originalHireDate: null,
      lastTerminationDate: null,

      fixedLeaveYearStart: null,
      bank1FirstLeaveUsageDate: null,
      bank2FirstLeaveUsageDate: null,
      bank3FirstLeaveUsageDate: null,
      bank4FirstLeaveUsageDate: null,
      bank5FirstLeaveUsageDate: null,
    });

    this.supervisorsArray.clear()
    this.supervisorsArray.push(this.initNewEEGroup());
  }

  private patchFormFields(id: number): void {
    const ee = this.data.employeeOptions.filter(eo => eo.id === id);
  
    if (id != null && ee.length === 1) {
      this.service
        .getEmployeeSelectionDetails(id, this.data.caseDetails.leaveInformation.caseId)
        .pipe(
          indicate(this.isLoading$),
          finalize(()=>{
            this.form.patchValue({
              employeeName: this.updateEERecord.fullName,
              workEmail:  this.updateEERecord.workEmail,
              workEmailChecked: this.updateEERecord.primaryEmailIsPersonal == false
                ? true
                : this.updateEERecord.primaryEmailIsPersonal == true
                  ? false
                  : null,
              personalEmail:  this.updateEERecord.personalEmail,
              personalEmailChecked: this.updateEERecord.primaryEmailIsPersonal == true
                ? true
                : this.updateEERecord.primaryEmailIsPersonal == false
                  ? false
                  : null,
              workPhone: this.updateEERecord.workPhone,
              personalPhone: this.updateEERecord.personalPhone,

              workLocation: this.updateEERecord.workLocation,
              jobTitle: this.updateEERecord.jobTitle,
              city: this.updateEERecord.city,
              stateId: this.updateEERecord.stateId,
              hrManagerId: +this.updateEERecord.hrManager?.id,

              employmentStatus: this.updateEERecord.employmentStatus,
              employeeType: this.updateEERecord.employeeType,
              payType: this.updateEERecord.payType,

              hireDate: this.updateEERecord.hireDate,
              originalHireDate: this.updateEERecord.originalHireDate,
              lastTerminationDate: this.updateEERecord.lastTerminationDate,

              fixedLeaveYearStart: this.updateEERecord.fixedLeaveYearStart,
              bank1FirstLeaveUsageDate: this.updateEERecord.bank1FirstLeaveUsageDate,
              bank2FirstLeaveUsageDate: this.updateEERecord.bank2FirstLeaveUsageDate,
              bank3FirstLeaveUsageDate: this.updateEERecord.bank3FirstLeaveUsageDate,
              bank4FirstLeaveUsageDate: this.updateEERecord.bank4FirstLeaveUsageDate,
              bank5FirstLeaveUsageDate: this.updateEERecord.bank5FirstLeaveUsageDate,
            });

            // handle patching supervisors
            for (let i = 0; i < this.updateEERecord.supervisors?.length; i++) {
              let formGroup: FormGroup<NewEmployeeGroupForm> = this.supervisorsArray.at(i);
              if (!formGroup) {
                formGroup = this.addSupervisorFormGroup();
              }
              formGroup.patchValue({employeeId: this.updateEERecord.supervisors[i].id as number});
            }

            this.missingDetailsErrorsSetTimeout(this.updateEERecord.missingDetails);
          }))
        .subscribe((res)=>{
          this.updateEERecord = res;
        });
    }
    else if (!this.isNewEmployee) {
      this.clearFormFields();
    };
  }

  private openDuplicateDetectedDialog(
    duplicateDetectedDialog: DuplicateDetectedDialog,
    overrideUpdateConf: boolean): void {
    const dialogConfig: MatDialogConfig = {
      id: DuplicateDetectedDialogComponent.componentName,
      width: '550px',
      disableClose: false,
      data: {
        sysText: this.data.duplicateDetectedSysText,
        duplicateDetectedDialog: duplicateDetectedDialog,
      }
    };

    this.dialog.open(DuplicateDetectedDialogComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.onSubmit(true, overrideUpdateConf);
        }
      });
  }

  private openUpdateConfirmationDialog(
    updateConfirmationDialog: UpdateConfirmationDialog, 
    overrideDuplicates: boolean,
    overrideUpdateConf: boolean
    ): void {
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      width: "450px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText: this.data.updateConfSysText,
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean | UpdateConfirmationDialogAction) => {
        // handle submit and add supervisor roles
        if (res === UpdateConfirmationDialogAction.Yes) {
          this.onSubmit(overrideDuplicates, overrideUpdateConf, true);
        }
        // handle submit and don't add supervisor roles
        else if (res === UpdateConfirmationDialogAction.No) {
          this.onSubmit(overrideDuplicates, overrideUpdateConf, false);
        }
        // handle confirmation
        else if (res === true) {
          this.onSubmit(overrideDuplicates, true);
        }
      });
  }

  private openPendingChangesDialog(
    overrideDuplicates: boolean, 
    overrideUpdateConf: boolean, 
    addSupervisorRoles: boolean): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px',
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.onSubmit(overrideDuplicates, overrideUpdateConf, addSupervisorRoles);
        }
      });
  }

  private handleNotifyToggle() {
    var showToggle = this.showNotifyToggle();

    this.showNotifyToggle$.next(showToggle);
  }
}